import React, { useEffect, useState } from "react";
import * as actions from "../Common/actions";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Box, Paper, Button, TextField } from "@mui/material";
import CustomAddModal from "../Common/CustomAddModal";
import { toastSuccess, toastError, checkUserDetailsSession, setUserDetailsSession, clearUserDetailsSession } from "../../utils";
import moment from "moment";
import UserDetailsPasswordModal from './UserDetailsPasswordModal';

// Helper function to calculate time ago with detailed breakdown
const getTimeAgo = (dateString) => {
  if (!dateString) return "-";

  const updatedDate = new Date(dateString);
  const now = new Date();

  let years = now.getFullYear() - updatedDate.getFullYear();
  let months = now.getMonth() - updatedDate.getMonth();
  let days = now.getDate() - updatedDate.getDate();

  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += lastMonth.getDate();
  }

  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }

  // Build the result string
  const parts = [];

  if (years > 0) {
    parts.push(`${years} year${years > 1 ? 's' : ''}`);
  }
  if (months > 0) {
    parts.push(`${months} month${months > 1 ? 's' : ''}`);
  }
  if (days > 0) {
    parts.push(`${days} day${days > 1 ? 's' : ''}`);
  }

  if (parts.length === 0) {
    return "Today";
  }

  return parts.join(', ');
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

function ViewUserDetails(props) {
  const params = useParams();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState({});
  const [bankDetails, setBankDetails] = useState({});
  const [openRevenue, setOpenRevenue] = useState(false);
  const [addRevenue, setAddRevenue] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const sessionValid = checkUserDetailsSession();
    if (sessionValid) {
      setIsAuthenticated(true);
      getUserDetailsById();
    } else {
      setShowPasswordModal(true);
      clearUserDetailsSession();
    }
  }, []);

  // Periodic session validation every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      const sessionValid = checkUserDetailsSession();
      if (!sessionValid) {
        setIsAuthenticated(false);
        setShowPasswordModal(true);
        setPasswordError('Your session has expired. Please re-enter the password.');
      }
    }, 300000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // Check session when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const sessionValid = checkUserDetailsSession();
        if (!sessionValid) {
          setIsAuthenticated(false);
          setShowPasswordModal(true);
          setPasswordError('Your session has expired. Please re-enter the password.');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  const getUserDetailsById = () => {
    props.setLoader(true);
    actions
      .getUserDetailsByIdForAdmin(params.id)
      .then((res) => {
        props.setLoader(false);
        setUserDetails(res.userDetails);
        setBankDetails(res.bankDetails);
        setAddRevenue(res.userDetails.revenue);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load user details');
        navigate("/user_details");
      });
  };

  const handlePasswordVerification = (password) => {
    setPasswordLoading(true);
    setPasswordError('');

    actions.verifyUserDetailsPassword({ password })
      .then((res) => {
        setPasswordLoading(false);
        setShowPasswordModal(false);
        setIsAuthenticated(true);
        setUserDetailsSession(Date.now());
        getUserDetailsById(); // Load data after authentication
        toastSuccess('Access granted');
      })
      .catch((err) => {
        setPasswordLoading(false);
        setPasswordError(err?.message || 'Invalid password. Please try again.');
      });
  };

  const onUpdateRevenue = () => {
    props.setLoader(true);
    actions
      .addRevenueAdmin({ user_id: params.id, revenue: addRevenue })
      .then((res) => {
        setOpenRevenue(false);
        getUserDetailsById();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to add revenue');
      });
  };

  const {
    name = "",
    email = "",
    phone = "",
    address = "",
    created_at = "",
    ip = "",
    revenue = "",
    revoked = "",
    total_releases = "",
    total_drafts = "",
    total_artist = "",
    total_reports_requested = "",
    total_tickets_created = "",
    total_open_tickets = "",
  } = userDetails;

  const {
    bank_name = "",
    ifsc_code = "",
    account_number = "",
    holder_name = "",
    branch_name = "",
    pan = "",
    other_details = "",
    updated_on = "",
  } = bankDetails;

  // Don't render content until authenticated
  if (!isAuthenticated) {
    return (
      <UserDetailsPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
    );
  }

  return (
    <>
      <UserDetailsPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
      <Grid container className="view-user-details-container">
      <Grid item xs={12} sm={12} md={8}>
        <Box p={1} className="box-container">
          <h3>User Details</h3>
          <Paper>
            <Box p={1}>
              <div className="details-div">
                <p>
                  <label>Name:</label> <span>{name}</span>
                </p>
                <p>
                  <label>Email:</label> <span>{email}</span>
                </p>
                <p>
                  <label>Address:</label> <span>{address}</span>
                </p>
                <p>
                  <label>Phone:</label> <span>{phone}</span>
                </p>
                <p>
                  <label>Revenue:</label> <span>${revenue}</span>
                </p>
                <p>
                  <label>Signup Date:</label>{" "}
                  <span>
                    {moment(created_at).format("DD/MM/yyyy hh:mm:ss A")}
                  </span>
                </p>
                <p>
                  <label>IP:</label> <span>{ip}</span>
                </p>
                <p>
                  <label>Revoked:</label>{" "}
                  <span>{revoked === 0 ? "No" : "Yes"}</span>
                </p>
                <p>
                  <label>Total Releases:</label> <span>{total_releases}</span>
                </p>
                <p>
                  <label>Total Drafts:</label> <span>{total_drafts}</span>
                </p>
                <p>
                  <label>Total Artist:</label> <span>{total_artist}</span>
                </p>
                <p>
                  <label>Total Reports Requested:</label>{" "}
                  <span>{total_reports_requested}</span>
                </p>
                <p>
                  <label>Total Tickets Created:</label>{" "}
                  <span>{total_tickets_created}</span>
                </p>
                <p>
                  <label>Total Open Tickets:</label>{" "}
                  <span>{total_open_tickets}</span>
                </p>
              </div>
            </Box>
          </Paper>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12} md={4}>
        <Box p={1} className="box-container">
          <h3>Bank Details</h3>
          <Paper>
            <Box p={1}>
              <div className="details-div">
                <p>
                  <label>Bank Name:</label> <span>{bank_name}</span>
                </p>
                <p>
                  <label>IFSC Code:</label> <span>{ifsc_code}</span>
                </p>
                <p>
                  <label>Account Number:</label> <span>{account_number}</span>
                </p>
                <p>
                  <label>Holder Name:</label> <span>{holder_name}</span>
                </p>
                <p>
                  <label>Branch Name:</label> <span>{branch_name}</span>
                </p>
                <p>
                  <label>PAN Number:</label> <span>{pan}</span>
                </p>
                <p>
                  <label>Other Details:</label> <span>{other_details}</span>
                </p>
                <p>
                  <label>Bank Updated On:</label> <span>{formatDate(updated_on)}</span>
                </p>
                <p>
                  <label>Ago:</label> <span style={{ color: "red" }}>{getTimeAgo(updated_on)}</span>
                </p>
              </div>
            </Box>
          </Paper>
        </Box>
        <Box textAlign="center" mt={1}>
          <Button
            className="add-revenue-button"
            onClick={() => setOpenRevenue(true)}
          >
            Add Revenue
          </Button>
        </Box>
      </Grid>

      <CustomAddModal
        open={openRevenue}
        title="Add/Update Revenue"
        handleClose={() => setOpenRevenue(false)}
        onSubmitConfirm={onUpdateRevenue}
        saveLabel="Update"
      >
        <TextField
          type="number"
          variant="outlined"
          fullWidth
          value={addRevenue}
          onChange={(e) => setAddRevenue(e.target.value)}
          placeholder="Add Revenue"
        />
      </CustomAddModal>
    </Grid>
    </>
  );
}

export default ViewUserDetails;
